import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { QuotaUpgradeDialog } from "@/components/QuotaUpgradeDialog";
import { useQuotaError } from "@/hooks/useQuotaError";
import { CAPABILITIES } from "@/lib/quotas";
import { useCandidate } from "@/context/CandidateContext";
import { useAuth } from "@/hooks/useAuth";
import {
  generateCareerPath,
  type CareerNode,
  type CareerCategory,
} from "@/server/generate-career-path";
import { supabase } from "@/integrations/supabase/client";
import { createGoal, fetchGoals, type GoalRow } from "@/lib/queries/goals";

export const Route = createFileRoute("/app/career-path")({
  beforeLoad: () => {
    throw redirect({ to: "/app/tools", search: { tab: "career-path" } });
  },
  component: () => null,
});

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

export function CareerPathPanel({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const theme = useTheme();
  const { profile } = useCandidate();
  const { user } = useAuth();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<CareerNode | null>(null);
  const [width, setWidth] = useState<number>(800);
  const [tree, setTree] = useState<CareerNode | null>(null);
  const [roleTitle, setRoleTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cached, setCached] = useState<boolean>(false);
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [savingGoal, setSavingGoal] = useState(false);
  const quotaDialog = useQuotaError(CAPABILITIES.career_path);

  // Load existing goals to show "saved" state on already-saved nodes
  useEffect(() => {
    if (!user) return;
    fetchGoals(user.id).then(setGoals).catch(() => {
      // benign — keep empty
    });
  }, [user]);

  const isNodeSaved = (nodeId: string) => goals.some((g) => g.source_node_id === nodeId);

  const handleSaveAsGoal = async () => {
    if (!user || !selected) return;
    if (isNodeSaved(selected.id)) {
      toast("Already saved as a goal.");
      return;
    }
    setSavingGoal(true);
    try {
      const created = await createGoal(user.id, {
        role: selected.role,
        category: selected.category,
        rationale: selected.rationale,
        salaryBand: selected.salaryBand ?? null,
        requiredSkills: selected.requiredSkills,
        sourceNodeId: selected.id,
      });
      setGoals((g) => [created, ...g]);
      toast.success(`Saved "${selected.role}" as a career goal.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save goal.");
    } finally {
      setSavingGoal(false);
    }
  };


  const hasProfile =
    profile.verifiedSkills.length > 0 ||
    profile.resume.chronologicalExperience.length > 0;

  // Initial load — try cached
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await generateCareerPath({ data: {}, headers });
        if (!mounted) return;
        setTree(res.tree);
        setRoleTitle(res.roleTitle);
        setCached(res.cached);
      } catch (e) {
        if (!mounted) return;
        // No cached path yet is OK — user clicks Generate
        const msg = e instanceof Error ? e.message : "Failed to load career path.";
        // Don't show error if it's just empty cache
        if (!msg.toLowerCase().includes("did not include")) {
          // benign — leave empty
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleGenerate = async (force: boolean) => {
    setGenerating(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await generateCareerPath({ data: { force }, headers });
      setTree(res.tree);
      setRoleTitle(res.roleTitle);
      setCached(res.cached);
      setSelected(null);
    } catch (e) {
      if (quotaDialog.handle(e)) return;
      setError(e instanceof Error ? e.message : "Failed to generate career path.");
    } finally {
      setGenerating(false);
    }
  };

  const categoryColor = (cat: CareerCategory): string => {
    switch (cat) {
      case "current":
        return theme.palette.primary.main;
      case "Similar":
        return theme.palette.info.main;
      case "Lateral":
        return theme.palette.warning.main;
      case "Advancement":
        return theme.palette.success.main;
      case "Feeder":
        return theme.palette.secondary.main;
    }
  };

  // ResizeObserver for responsive SVG width — useLayoutEffect so the first
  // measurement is captured synchronously before D3 paints with a stale width.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Initial synchronous measure
    const initial = el.getBoundingClientRect().width;
    if (initial > 0) setWidth(initial);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [tree]);

  // D3 render
  useEffect(() => {
    if (!svgRef.current || !tree) return;
    const svgEl = svgRef.current;
    const height = 560;

    const root = d3.hierarchy<CareerNode>(tree);
    const treeLayout = d3.tree<CareerNode>().nodeSize([60, 220]);
    treeLayout(root);

    const nodes = root.descendants();
    const links = root.links();

    const xs = nodes.map((n) => n.x ?? 0);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const treeHeight = maxX - minX;

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${width} ${height}`);

    const g = svg.append("g").attr("class", "zoom-layer");
    const inner = g
      .append("g")
      .attr(
        "transform",
        `translate(80, ${height / 2 - (minX + treeHeight / 2)})`,
      );

    const linkGen = d3
      .linkHorizontal<d3.HierarchyLink<CareerNode>, d3.HierarchyPointNode<CareerNode>>()
      .x((d) => d.y ?? 0)
      .y((d) => d.x ?? 0);

    inner
      .append("g")
      .attr("fill", "none")
      .attr("stroke", theme.palette.divider)
      .attr("stroke-width", 1.5)
      .selectAll("path")
      .data(links)
      .join("path")
      .attr("d", (d) => linkGen(d as unknown as d3.HierarchyLink<CareerNode>) as string);

    const nodeG = inner
      .append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .attr("transform", (d) => `translate(${d.y ?? 0}, ${d.x ?? 0})`)
      .style("cursor", "pointer")
      .on("click", (_event, d) => setSelected(d.data));

    nodeG
      .append("circle")
      .attr("r", 8)
      .attr("fill", (d) => categoryColor(d.data.category))
      .attr("stroke", (d) =>
        selected?.id === d.data.id ? theme.palette.text.primary : theme.palette.background.paper,
      )
      .attr("stroke-width", (d) => (selected?.id === d.data.id ? 3 : 2))
      .on("mouseenter", function () {
        d3.select(this).transition().duration(120).attr("r", 10);
      })
      .on("mouseleave", function () {
        d3.select(this).transition().duration(120).attr("r", 8);
      });

    nodeG
      .filter((d) => Boolean(d.data.brightOutlook))
      .append("text")
      .attr("x", 0)
      .attr("y", -10)
      .attr("text-anchor", "middle")
      .attr("font-size", 11)
      .attr("aria-label", "Bright Outlook")
      .text("☀");

    nodeG
      .append("text")
      .attr("x", 14)
      .attr("dy", "0.32em")
      .attr("font-size", 12)
      .attr("font-family", "inherit")
      .attr("fill", theme.palette.text.primary)
      .text((d) =>
        d.data.medianWage != null
          ? `${d.data.role} · $${Math.round(d.data.medianWage / 1000)}k`
          : d.data.role,
      );

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.4, 2.5])
      .on("zoom", (event) => {
        g.attr("transform", event.transform.toString());
      });

    svg.call(zoom);
    (svgEl as unknown as { __resetZoom?: () => void }).__resetZoom = () => {
      svg.transition().duration(400).call(zoom.transform, d3.zoomIdentity);
    };
  }, [tree, theme, width, selected]);

  const handleResetZoom = () => {
    const el = svgRef.current as unknown as { __resetZoom?: () => void } | null;
    el?.__resetZoom?.();
  };

  // Skill gap calculation
  const verifiedSkillNames = useMemo(
    () => new Set(profile.verifiedSkills.map((s) => s.name.toLowerCase())),
    [profile.verifiedSkills],
  );

  const { haveSkills, learnSkills } = useMemo(() => {
    if (!selected) return { haveSkills: [] as string[], learnSkills: [] as string[] };
    const have: string[] = [];
    const learn: string[] = [];
    for (const s of selected.requiredSkills) {
      if (verifiedSkillNames.has(s.toLowerCase())) have.push(s);
      else learn.push(s);
    }
    return { haveSkills: have, learnSkills: learn };
  }, [selected, verifiedSkillNames]);

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Career path"
          subtitle={
            roleTitle
              ? `Personalised pathways from your role: ${roleTitle}`
              : "AI-mapped pathways from your verified profile."
          }
        />
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  mb: 2,
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                }}
              >
                <Box>
                  <Typography variant="h6">Pathways from your current role</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tree
                      ? "Drag to pan · scroll to zoom · click a node"
                      : "Click Generate to map your AI career tree"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  {tree && (
                    <>
                      <Chip
                        size="small"
                        label="Current"
                        sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
                      />
                      <Chip size="small" label="Similar" sx={{ bgcolor: theme.palette.info.main, color: theme.palette.info.contrastText }} />
                      <Chip size="small" label="Lateral" sx={{ bgcolor: theme.palette.warning.main, color: theme.palette.warning.contrastText }} />
                      <Chip size="small" label="Advancement" sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText }} />
                      <Chip size="small" label="Feeder" sx={{ bgcolor: theme.palette.secondary.main, color: theme.palette.secondary.contrastText }} />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RestartAltIcon />}
                        onClick={handleResetZoom}
                      >
                        Reset
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={generating ? <CircularProgress size={14} /> : <RefreshIcon />}
                        onClick={() => handleGenerate(true)}
                        disabled={generating || !hasProfile}
                      >
                        Regenerate
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!hasProfile && !tree && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Add skills or experience on your Profile first — the AI uses them to map your career options.
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: "grid", placeItems: "center", height: 560 }}>
                  <CircularProgress />
                </Box>
              ) : !tree ? (
                <Box sx={{ display: "grid", placeItems: "center", height: 560, gap: 2 }}>
                  <Typography color="text.secondary">No career path yet.</Typography>
                  <Button
                    variant="contained"
                    startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={() => handleGenerate(true)}
                    disabled={generating || !hasProfile}
                  >
                    {generating ? "Mapping pathways…" : "Generate career path"}
                  </Button>
                </Box>
              ) : (
                <Box
                  ref={containerRef}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    height: 560,
                    borderRadius: 2,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(0,0,0,0.02)",
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                  }}
                >
                  <svg
                    ref={svgRef}
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                    style={{ display: "block", maxWidth: "100%", overflow: "hidden" }}
                  />
                </Box>
              )}

              {cached && tree && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Showing cached path. Click Regenerate to refresh from your latest profile.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Node details
              </Typography>
              {!selected ? (
                <Typography color="text.secondary" variant="body2">
                  Click any node to see required skills and your gap.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {selected.role}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: "center" }}>
                      <Chip
                        size="small"
                        label={selected.category}
                        sx={{
                          bgcolor: categoryColor(selected.category),
                          color:
                            selected.category === "Lateral"
                              ? theme.palette.warning.contrastText
                              : selected.category === "Similar"
                                ? theme.palette.info.contrastText
                                : selected.category === "Advancement"
                                  ? theme.palette.success.contrastText
                                  : selected.category === "Feeder"
                                    ? theme.palette.secondary.contrastText
                                    : theme.palette.primary.contrastText,
                        }}
                      />
                      {selected.salaryBand && (
                        <Typography variant="caption" color="text.secondary">
                          {selected.salaryBand}
                        </Typography>
                      )}
                      {selected.brightOutlook && (
                        <Chip
                          size="small"
                          label="Bright Outlook ☀"
                          color="warning"
                          variant="outlined"
                          title={
                            (selected.brightOutlookCategories ?? []).join(", ") ||
                            "Projected to grow rapidly or have many openings"
                          }
                        />
                      )}
                    </Stack>
                    {(selected.medianWage != null ||
                      (selected.wageRange &&
                        (selected.wageRange.low != null || selected.wageRange.high != null))) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {selected.medianWage != null
                          ? `Median ~$${Math.round(selected.medianWage / 1000)}k/yr`
                          : ""}
                        {selected.wageRange?.low != null && selected.wageRange?.high != null
                          ? ` · 10–90th: $${Math.round(selected.wageRange.low / 1000)}k–$${Math.round(selected.wageRange.high / 1000)}k`
                          : ""}
                        {" · source: O*NET"}
                      </Typography>
                    )}
                  </Box>

                  {selected.rationale && (
                    <Typography variant="body2" color="text.secondary">
                      {selected.rationale}
                    </Typography>
                  )}

                  <Divider />

                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Skills you have ({haveSkills.length})
                    </Typography>
                    {haveSkills.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        None yet for this role.
                      </Typography>
                    ) : (
                      <Stack direction="row" useFlexGap sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                        {haveSkills.map((s) => (
                          <Chip key={s} size="small" label={s} color="success" variant="filled" />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      Skills to learn ({learnSkills.length})
                    </Typography>
                    {learnSkills.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        You're fully qualified for this node. 🎉
                      </Typography>
                    ) : (
                      <Stack direction="row" useFlexGap sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                        {learnSkills.map((s) => (
                          <Chip key={s} size="small" label={s} color="warning" variant="outlined" />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Divider />

                  {(() => {
                    const isBucket =
                      selected.id === "sim" ||
                      selected.id === "lat" ||
                      selected.id === "adv" ||
                      selected.id === "fdr";
                    const isCurrent = selected.category === "current";
                    const disabled =
                      savingGoal || !user || isCurrent || isBucket || isNodeSaved(selected.id);
                    return (
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={disabled}
                        startIcon={
                          isNodeSaved(selected.id) ? (
                            <CheckIcon />
                          ) : savingGoal ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <BookmarkAddOutlinedIcon />
                          )
                        }
                        onClick={handleSaveAsGoal}
                      >
                        {isNodeSaved(selected.id)
                          ? "Saved as goal"
                          : isCurrent
                            ? "Current role"
                            : isBucket
                              ? "Pick a specific role"
                              : savingGoal
                                ? "Saving…"
                                : "Save as career goal"}
                      </Button>
                    );
                  })()}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <QuotaUpgradeDialog
        open={quotaDialog.open}
        onClose={quotaDialog.close}
        quota={quotaDialog.quota}
        message={quotaDialog.message}
      />
    </Box>
  );
}
